import {
  defaultPdfCodeTheme,
  type PdfCodeTheme,
  type PdfResolvedCodeTheme,
} from "./code-themes";

export const PDF_THEME_IDS = [
  "github",
  "solarized-light",
  "solarized-dark",
  "dracula",
  "one-dark",
  "sepia",
  "paper",
] as const;

export type PdfTheme = (typeof PDF_THEME_IDS)[number];

export type PdfThemeCssTokens = {
  page: string;
  text: string;
  heading: string;
  muted: string;
  border: string;
  borderSubtle: string;
  codeBg: string;
  markBg: string;
  tableHeaderBg: string;
  pageNumber: string;
  sheetBorder: string;
  sheetShadow: string;
  mermaidFallbackBg: string;
  mermaidFallbackBorder: string;
  mermaidFallbackText: string;
  katexErrorBg: string;
  katexErrorText: string;
};

export type PdfThemeMermaidConfig = {
  /** Curated palettes use Mermaid "base" so themeVariables apply fully. */
  theme: "base";
  themeVariables: Record<string, string>;
};

export type PdfThemeDefinition = {
  id: PdfTheme;
  label: string;
  isDark: boolean;
  defaultCodeTheme: PdfResolvedCodeTheme;
  tokens: PdfThemeCssTokens;
  mermaid: PdfThemeMermaidConfig;
};

export const PDF_DIAGRAM_THEME_IDS = ["auto", ...PDF_THEME_IDS] as const;

export type PdfDiagramTheme = (typeof PDF_DIAGRAM_THEME_IDS)[number];

export const defaultPdfDiagramTheme: PdfDiagramTheme = "auto";

export function isPdfDiagramTheme(value: unknown): value is PdfDiagramTheme {
  return (
    typeof value === "string" &&
    PDF_DIAGRAM_THEME_IDS.includes(value as PdfDiagramTheme)
  );
}

/** Resolve which document theme's Mermaid palette to use. */
export function resolveDiagramThemePalette(
  documentTheme: PdfTheme,
  diagramTheme: PdfDiagramTheme = defaultPdfDiagramTheme,
): PdfTheme {
  return diagramTheme === "auto" ? documentTheme : diagramTheme;
}

/** Resolve which concrete code theme to use (Auto → document pairing). */
export function resolveCodeTheme(
  documentTheme: PdfTheme,
  codeTheme: PdfCodeTheme = defaultPdfCodeTheme,
): PdfResolvedCodeTheme {
  return codeTheme === "auto"
    ? getPdfThemeDefinition(documentTheme).defaultCodeTheme
    : codeTheme;
}

export const PDF_THEMES: readonly PdfThemeDefinition[] = [
  {
    id: "github",
    label: "GitHub",
    isDark: false,
    defaultCodeTheme: "github-light",
    tokens: {
      page: "#ffffff",
      text: "#24292f",
      heading: "#0f172a",
      muted: "#57606a",
      border: "#d0d7de",
      borderSubtle: "#d8dee4",
      codeBg: "#f6f8fa",
      markBg: "#fff3bf",
      tableHeaderBg: "#f6f8fa",
      pageNumber: "#57606a",
      sheetBorder: "#d4d4d8",
      sheetShadow: "0 1px 2px rgb(0 0 0 / 0.04)",
      mermaidFallbackBg: "#fff8c5",
      mermaidFallbackBorder: "#d4a72c",
      mermaidFallbackText: "#7d4e00",
      katexErrorBg: "#fff8c5",
      katexErrorText: "#9a6700",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        // Cool blue wash — distinct from warm cream themes.
        primaryColor: "#ddf4ff",
        primaryTextColor: "#24292f",
        primaryBorderColor: "#0969da",
        secondaryColor: "#f6f8fa",
        secondaryTextColor: "#24292f",
        tertiaryColor: "#ffffff",
        tertiaryTextColor: "#24292f",
        lineColor: "#57606a",
        textColor: "#24292f",
        background: "#ffffff",
        mainBkg: "#ddf4ff",
        nodeBorder: "#0969da",
        clusterBkg: "#f6f8fa",
        titleColor: "#0f172a",
        edgeLabelBackground: "#ffffff",
        pie1: "#0969da",
        pie2: "#1a7f37",
        pie3: "#cf222e",
        pie4: "#8250df",
        pieSectionTextColor: "#ffffff",
        pieTitleTextColor: "#0f172a",
        pieLegendTextColor: "#24292f",
      },
    },
  },
  {
    id: "solarized-light",
    label: "Solarized Light",
    isDark: false,
    defaultCodeTheme: "solarized-light",
    tokens: {
      page: "#fdf6e3",
      text: "#586e75",
      heading: "#073642",
      muted: "#839496",
      border: "#eee8d5",
      borderSubtle: "#e6dfc8",
      codeBg: "#eee8d5",
      markBg: "#eee8d5",
      tableHeaderBg: "#eee8d5",
      pageNumber: "#93a1a1",
      sheetBorder: "#d6cfc0",
      sheetShadow: "0 1px 2px rgb(101 123 131 / 0.12)",
      mermaidFallbackBg: "#eee8d5",
      mermaidFallbackBorder: "#b58900",
      mermaidFallbackText: "#657b83",
      katexErrorBg: "#eee8d5",
      katexErrorText: "#cb4b16",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        // Soft cyan wash + Solarized teal border (not another beige slab).
        primaryColor: "#d5ebe6",
        primaryTextColor: "#073642",
        primaryBorderColor: "#2aa198",
        secondaryColor: "#fdf6e3",
        secondaryTextColor: "#073642",
        tertiaryColor: "#eee8d5",
        tertiaryTextColor: "#073642",
        lineColor: "#586e75",
        textColor: "#073642",
        background: "#fdf6e3",
        mainBkg: "#d5ebe6",
        nodeBorder: "#2aa198",
        clusterBkg: "#eee8d5",
        titleColor: "#073642",
        edgeLabelBackground: "#fdf6e3",
        // Deepened accents so a single light pie label meets AA on every slice.
        pie1: "#1a648f",
        pie2: "#5a6800",
        pie3: "#a33d12",
        pie4: "#555a9c",
        pieSectionTextColor: "#ffffff",
        pieTitleTextColor: "#073642",
        pieLegendTextColor: "#073642",
      },
    },
  },
  {
    id: "solarized-dark",
    label: "Solarized Dark",
    isDark: true,
    defaultCodeTheme: "solarized-dark",
    tokens: {
      page: "#002b36",
      text: "#eee8d5",
      heading: "#fdf6e3",
      muted: "#93a1a1",
      border: "#073642",
      borderSubtle: "#0a4a58",
      codeBg: "#073642",
      markBg: "#073642",
      tableHeaderBg: "#073642",
      pageNumber: "#586e75",
      sheetBorder: "#073642",
      sheetShadow: "0 1px 2px rgb(0 0 0 / 0.35)",
      mermaidFallbackBg: "#073642",
      mermaidFallbackBorder: "#b58900",
      mermaidFallbackText: "#b58900",
      katexErrorBg: "#073642",
      katexErrorText: "#cb4b16",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        darkMode: "true",
        // Teal Solarized base02 — reads clearly apart from purple/blue dark themes.
        primaryColor: "#0a4a58",
        primaryTextColor: "#fdf6e3",
        primaryBorderColor: "#2aa198",
        secondaryColor: "#002b36",
        secondaryTextColor: "#eee8d5",
        tertiaryColor: "#073642",
        tertiaryTextColor: "#eee8d5",
        lineColor: "#839496",
        textColor: "#eee8d5",
        background: "#002b36",
        mainBkg: "#0a4a58",
        nodeBorder: "#2aa198",
        clusterBkg: "#073642",
        titleColor: "#eee8d5",
        edgeLabelBackground: "#002b36",
        pie1: "#1a648f",
        pie2: "#5a6800",
        pie3: "#a33d12",
        pie4: "#555a9c",
        pieSectionTextColor: "#ffffff",
        pieTitleTextColor: "#fdf6e3",
        pieLegendTextColor: "#eee8d5",
      },
    },
  },
  {
    id: "dracula",
    label: "Dracula",
    isDark: true,
    defaultCodeTheme: "dracula",
    tokens: {
      page: "#282a36",
      text: "#f8f8f2",
      heading: "#f8f8f2",
      muted: "#6272a4",
      border: "#44475a",
      borderSubtle: "#6272a4",
      codeBg: "#21222c",
      markBg: "#44475a",
      tableHeaderBg: "#21222c",
      pageNumber: "#6272a4",
      sheetBorder: "#44475a",
      sheetShadow: "0 1px 2px rgb(0 0 0 / 0.4)",
      mermaidFallbackBg: "#44475a",
      mermaidFallbackBorder: "#ffb86c",
      mermaidFallbackText: "#ffb86c",
      katexErrorBg: "#44475a",
      katexErrorText: "#ffb86c",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        darkMode: "true",
        // Purple-washed fill so flowcharts are not just another charcoal slab.
        primaryColor: "#4b3968",
        primaryTextColor: "#f8f8f2",
        primaryBorderColor: "#bd93f9",
        secondaryColor: "#282a36",
        secondaryTextColor: "#f8f8f2",
        tertiaryColor: "#21222c",
        tertiaryTextColor: "#f8f8f2",
        lineColor: "#6272a4",
        textColor: "#f8f8f2",
        background: "#282a36",
        mainBkg: "#4b3968",
        nodeBorder: "#bd93f9",
        clusterBkg: "#21222c",
        titleColor: "#f8f8f2",
        edgeLabelBackground: "#282a36",
        pie1: "#bd93f9",
        pie2: "#50fa7b",
        pie3: "#ffb86c",
        pie4: "#8be9fd",
        pieSectionTextColor: "#282a36",
        pieTitleTextColor: "#f8f8f2",
        pieLegendTextColor: "#f8f8f2",
      },
    },
  },
  {
    id: "one-dark",
    label: "One Dark",
    isDark: true,
    defaultCodeTheme: "one-dark-pro",
    tokens: {
      page: "#282c34",
      text: "#abb2bf",
      heading: "#e6e6e6",
      muted: "#5c6370",
      border: "#3e4451",
      borderSubtle: "#4b5263",
      codeBg: "#21252b",
      markBg: "#3e4451",
      tableHeaderBg: "#21252b",
      pageNumber: "#5c6370",
      sheetBorder: "#3e4451",
      sheetShadow: "0 1px 2px rgb(0 0 0 / 0.4)",
      mermaidFallbackBg: "#3e4451",
      mermaidFallbackBorder: "#e5c07b",
      mermaidFallbackText: "#e5c07b",
      katexErrorBg: "#3e4451",
      katexErrorText: "#e5c07b",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        darkMode: "true",
        // Cool blue-grey fill + bright blue border vs Solarized teal / Dracula purple.
        primaryColor: "#3a4f66",
        primaryTextColor: "#f3f4f6",
        primaryBorderColor: "#61afef",
        secondaryColor: "#282c34",
        secondaryTextColor: "#f3f4f6",
        tertiaryColor: "#21252b",
        tertiaryTextColor: "#f3f4f6",
        lineColor: "#5c6370",
        textColor: "#e6e6e6",
        background: "#282c34",
        mainBkg: "#3a4f66",
        nodeBorder: "#61afef",
        clusterBkg: "#21252b",
        titleColor: "#e6e6e6",
        edgeLabelBackground: "#282c34",
        pie1: "#61afef",
        pie2: "#98c379",
        pie3: "#e5c07b",
        pie4: "#c678dd",
        pieSectionTextColor: "#282c34",
        pieTitleTextColor: "#e6e6e6",
        pieLegendTextColor: "#e6e6e6",
      },
    },
  },
  {
    id: "sepia",
    label: "Sepia",
    isDark: false,
    defaultCodeTheme: "sepia",
    tokens: {
      page: "#f4ecd8",
      text: "#5b4636",
      heading: "#3f2e22",
      muted: "#8a7260",
      border: "#d4c4a8",
      borderSubtle: "#e0d2b8",
      codeBg: "#ebe1c8",
      markBg: "#e8d9b0",
      tableHeaderBg: "#ebe1c8",
      pageNumber: "#8a7260",
      sheetBorder: "#d4c4a8",
      sheetShadow: "0 1px 2px rgb(91 70 54 / 0.12)",
      mermaidFallbackBg: "#ebe1c8",
      mermaidFallbackBorder: "#c4a574",
      mermaidFallbackText: "#5b4636",
      katexErrorBg: "#ebe1c8",
      katexErrorText: "#8a5040",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        // Warm brown wash — clearly sepia, not Solarized cyan or Paper grey.
        primaryColor: "#e6d3a8",
        primaryTextColor: "#3f2e22",
        primaryBorderColor: "#a67c52",
        secondaryColor: "#f4ecd8",
        secondaryTextColor: "#3f2e22",
        tertiaryColor: "#e8d9b0",
        tertiaryTextColor: "#3f2e22",
        lineColor: "#8a7260",
        textColor: "#3f2e22",
        background: "#f4ecd8",
        mainBkg: "#e6d3a8",
        nodeBorder: "#a67c52",
        clusterBkg: "#ebe1c8",
        titleColor: "#3f2e22",
        edgeLabelBackground: "#f4ecd8",
        pie1: "#6f5134",
        pie2: "#8a5040",
        pie3: "#7a5a32",
        pie4: "#5b4636",
        pieSectionTextColor: "#ffffff",
        pieTitleTextColor: "#3f2e22",
        pieLegendTextColor: "#3f2e22",
      },
    },
  },
  {
    id: "paper",
    label: "Paper",
    isDark: false,
    defaultCodeTheme: "github-light",
    tokens: {
      page: "#f7f3ea",
      text: "#3d3a34",
      heading: "#2a2824",
      muted: "#6e6a62",
      border: "#ddd6c8",
      borderSubtle: "#e8e2d6",
      codeBg: "#efe9dc",
      markBg: "#e8e0ce",
      tableHeaderBg: "#efe9dc",
      pageNumber: "#6e6a62",
      sheetBorder: "#d6cfc0",
      sheetShadow: "0 1px 2px rgb(61 58 52 / 0.1)",
      mermaidFallbackBg: "#efe9dc",
      mermaidFallbackBorder: "#c4b89a",
      mermaidFallbackText: "#3d3a34",
      katexErrorBg: "#efe9dc",
      katexErrorText: "#8a6a40",
    },
    mermaid: {
      theme: "base",
      themeVariables: {
        // Neutral ink-on-paper: pale grey fill + charcoal border.
        primaryColor: "#ebe6dc",
        primaryTextColor: "#2a2824",
        primaryBorderColor: "#6e6a62",
        secondaryColor: "#f7f3ea",
        secondaryTextColor: "#2a2824",
        tertiaryColor: "#e8e0ce",
        tertiaryTextColor: "#2a2824",
        lineColor: "#6e6a62",
        textColor: "#2a2824",
        background: "#f7f3ea",
        mainBkg: "#ebe6dc",
        nodeBorder: "#6e6a62",
        clusterBkg: "#efe9dc",
        titleColor: "#2a2824",
        edgeLabelBackground: "#f7f3ea",
        pie1: "#6e6a62",
        pie2: "#6f5533",
        pie3: "#3d3a34",
        pie4: "#736d5c",
        pieSectionTextColor: "#ffffff",
        pieTitleTextColor: "#2a2824",
        pieLegendTextColor: "#2a2824",
      },
    },
  },
] as const;

const themeById = new Map(
  PDF_THEMES.map((theme) => [theme.id, theme] as const),
);

export function isPdfTheme(value: unknown): value is PdfTheme {
  return typeof value === "string" && PDF_THEME_IDS.includes(value as PdfTheme);
}

export function getPdfThemeDefinition(id: PdfTheme): PdfThemeDefinition {
  return themeById.get(id) ?? PDF_THEMES[0];
}

export const defaultPdfTheme: PdfTheme = "github";

export function buildThemeCssVariables(tokens: PdfThemeCssTokens): string {
  return [
    `--md-page: ${tokens.page}`,
    `--md-fg: ${tokens.text}`,
    `--md-heading: ${tokens.heading}`,
    `--md-muted: ${tokens.muted}`,
    `--md-border: ${tokens.border}`,
    `--md-border-subtle: ${tokens.borderSubtle}`,
    `--md-code-bg: ${tokens.codeBg}`,
    `--md-mark-bg: ${tokens.markBg}`,
    `--md-table-header-bg: ${tokens.tableHeaderBg}`,
    `--md-page-number: ${tokens.pageNumber}`,
    `--md-sheet-border: ${tokens.sheetBorder}`,
    `--md-sheet-shadow: ${tokens.sheetShadow}`,
    `--md-mermaid-fallback-bg: ${tokens.mermaidFallbackBg}`,
    `--md-mermaid-fallback-border: ${tokens.mermaidFallbackBorder}`,
    `--md-mermaid-fallback-text: ${tokens.mermaidFallbackText}`,
    `--md-katex-error-bg: ${tokens.katexErrorBg}`,
    `--md-katex-error-text: ${tokens.katexErrorText}`,
  ].join(";\n  ");
}

export { defaultPdfCodeTheme };
